import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

export type SupportedLanguage = 'en' | 'hi';

type DictionaryResponse = {
  language: string;
  dictionary: Record<string, string>;
};

@Injectable({
  providedIn: 'root'
})
export class DbTranslationService {
  private readonly baseUrl = `${environment.apiUrl}/translations`;
  private dictionary: Record<string, string> = {};
  private language: SupportedLanguage = 'en';

  get currentLanguage(): SupportedLanguage {
    return this.language;
  }

  async initialize(language: SupportedLanguage): Promise<void> {
    this.language = language;
    await this.loadDictionary(language);
  }

  /**
   * Idempotent align for route changes: avoids redundant fetches when already loaded for HI.
   */
  async ensureAligned(language: SupportedLanguage): Promise<void> {
    if (this.language === language && (language === 'en' || Object.keys(this.dictionary).length > 0)) {
      return;
    }
    await this.initialize(language);
  }

  async setLanguage(language: SupportedLanguage): Promise<void> {
    if (this.language === language) return;
    this.language = language;
    await this.loadDictionary(language);
  }

  t(text: string): string {
    if (!text) return '';
    if (this.language === 'en') return text;

    // If full text is present as a key in DB, return it directly
    const direct = this.dictionary[text];
    if (direct) return direct;

    // Otherwise: dictionary-based word replacement (recommended option)
    return this.replaceWords(text);
  }

  /**
   * Returns HTML where only matched tokens are replaced and protected from Google Translate.
   * - Matched tokens: replaced with Hindi and wrapped in <span class="notranslate" translate="no">...</span>
   * - Unmatched tokens: left as-is (Google Translate can translate them)
   */
  mixedHtml(text: string): string {
    if (!text) return '';
    if (this.language === 'en') return this.escapeHtml(text);

    // If full sentence is present as a key in DB, protect whole output from Google Translate.
    const direct = this.dictionary[text];
    if (direct) {
      return `<span class="notranslate" translate="no">${this.escapeHtml(direct)}&nbsp;</span>`;
    }

    const pieces = this.splitPreservingSeparators(text);
    let out = '';
    for (let i = 0; i < pieces.length; i++) {
      const part = pieces[i];
      if (!part.isWord) {
        out += this.escapeHtml(part.value);
        continue;
      }

      const replacement = this.lookup(part.value);
      if (!replacement) {
        out += this.escapeHtml(part.value);
        continue;
      }

      out += `<span class="notranslate" translate="no">${this.escapeHtml(replacement)}&nbsp;</span>`;

      // Ensure a visible separator between a protected translated token and the next word.
      // This avoids cases like: <span>प्रिंस</span><font>मोदनवाल</font>
      // when the original text had no whitespace or when Translate collapses it.
      const next = pieces[i + 1];
      if (next) {
        const nextStartsLikeWord =
          next.isWord || (!next.isWord && /^[A-Za-z0-9]/.test(next.value));
        const nextHasLeadingWhitespace = !next.isWord && /^\s/.test(next.value);
        if (nextStartsLikeWord && !nextHasLeadingWhitespace) {
          out += ' ';
        }
      }
    }
    return out;
  }

  private lookup(word: string): string | null {
    if (!word) return null;
    // Exact match first
    const direct = this.dictionary[word];
    if (direct) return direct;

    // Common case: DB stored TitleCase while UI has different case.
    const lower = word.toLowerCase();
    for (const key of Object.keys(this.dictionary)) {
      if (key.toLowerCase() === lower) {
        return this.dictionary[key];
      }
    }
    return null;
  }

  private splitPreservingSeparators(text: string): Array<{ value: string; isWord: boolean }> {
    // Split into word tokens (letters/apostrophes) and separators.
    // This focuses on English-origin data; Hindi words will remain as separators/non-words and won't be reprocessed.
    const regex = /([A-Za-z][A-Za-z']*)/g;
    const out: Array<{ value: string; isWord: boolean }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        out.push({ value: text.slice(lastIndex, match.index), isWord: false });
      }
      out.push({ value: match[1], isWord: true });
      lastIndex = match.index + match[1].length;
    }
    if (lastIndex < text.length) {
      out.push({ value: text.slice(lastIndex), isWord: false });
    }
    return out;
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private replaceWords(text: string): string {
    let out = text;
    // Replace longer keys first to avoid partial replacements
    const keys = Object.keys(this.dictionary).sort((a, b) => b.length - a.length);
    for (const en of keys) {
      const hi = this.dictionary[en];
      if (!en || !hi) continue;
      const pattern = new RegExp(`\\b${this.escapeRegExp(en)}\\b`, 'g');
      out = out.replace(pattern, hi);
    }
    return out;
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private async loadDictionary(language: SupportedLanguage): Promise<void> {
    if (language === 'en') {
      this.dictionary = {};
      return;
    }
    const resp = await firstValueFrom(
      this.http.get<DictionaryResponse>(`${this.baseUrl}/dictionary?lang=${encodeURIComponent(language)}`)
    );
    this.dictionary = resp?.dictionary || {};
  }

  constructor(private http: HttpClient) { }
}

